import { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { Spinner } from '@/components/ui/spinner';
import { useSitesStore } from '@/features/sites/store/useSitesStore';
import { useRadiologistsStore } from '@/features/radiologists/store/useRadiologistsStore';
import { useAuth0 } from '@auth0/auth0-react';
import { useAuthStore } from '@/stores/useAuthStore';
import NoRoleScreen from '@/components/NoRoleScreen';
import { ROLE, hasPermission } from '@/lib/permissions';
import { ROUTES } from '@/lib/routes';

const KNOWN_ROLES = new Set<string>(Object.values(ROLE));

export default function ProtectedRoute() {
  const { isAuthenticated, isLoading: isAuth0Loading } = useAuth0();
  const roles = useAuthStore((s) => s.roles);
  const isRolesLoaded = useAuthStore((s) => s.isRolesLoaded);
  const sitesLoading = useSitesStore((s) => s.loading);
  const radiologistsLoading = useRadiologistsStore((s) => s.loading);
  const location = useLocation();
  const hasKnownRole = roles.some((role) => KNOWN_ROLES.has(role));
  // Sites and radiologists are schedule data; support-only roles can't read them.
  const canViewSchedule = hasPermission(roles, 'view_schedule');

  useEffect(() => {
    if (!isAuthenticated || !isRolesLoaded || !canViewSchedule) return;

    const sitesState = useSitesStore.getState();
    if (Object.keys(sitesState.sites).length === 0 && !sitesState.loading) {
      void sitesState.list();
    }

    const radiologistsState = useRadiologistsStore.getState();
    if (
      Object.keys(radiologistsState.radiologists).length === 0 &&
      !radiologistsState.loading
    ) {
      void radiologistsState.list();
    }
  }, [isAuthenticated, isRolesLoaded, canViewSchedule]);

  if (isAuth0Loading) {
    return (
      <div className="w-full h-dvh flex items-center justify-center">
        <Spinner className="size-6" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={ROUTES.SIGN_IN} state={{ from: location }} replace />;
  }

  if (!isRolesLoaded) {
    return (
      <div className="w-full h-dvh flex items-center justify-center">
        <Spinner className="size-6" />
      </div>
    );
  }

  if (!hasKnownRole) {
    return <NoRoleScreen />;
  }

  const isLoading = sitesLoading || radiologistsLoading;
  if (isLoading) {
    return (
      <div className="w-full h-dvh flex items-center justify-center">
        <Spinner className="size-6" />
      </div>
    );
  }

  return <Outlet />;
}
